import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

// Google Places Autocomplete on street line 1; fills city, state, postal code, street 2 when available.
export default class extends Controller {
  static values = {
    apiKey: String
  }

  static targets = [
    "street1",
    "street2",
    "city",
    "citySelect",
    "postalCode",
    "state",
    "stateSelect",
    "latitude",
    "longitude"
  ]

  async connect() {
    if (!this.apiKeyValue) return
    if (!this.hasStreet1Target) return

    await this.initAutocomplete()
  }

  disconnect() {
    if (this.autocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete)
    }
    this.autocomplete = null
    this.cancelHidePacFrame()
    this.unbindStreet1SuggestionEvents()
  }

  async initAutocomplete() {
    try {
      if (!window.google?.maps && !document.querySelector('script[src*="maps.googleapis.com"]')) {
        setOptions({ key: this.apiKeyValue })
      }

      const { Autocomplete } = await importLibrary("places")
      this.autocomplete = new Autocomplete(this.street1Target, {
        fields: ["address_components", "geometry"],
        types: ["address"],
        componentRestrictions: { country: "us" }
      })

      this.autocomplete.addListener("place_changed", () => this.onPlaceChanged())
      this.bindStreet1SuggestionEvents()

      // Google defaults to "Enter a location"; keep copy aligned with the app.
      this.street1Target.placeholder = "Enter an address"
    } catch (e) {
      console.warn("Address autocomplete failed to load:", e)
    }
  }

  onPlaceChanged() {
    const place = this.autocomplete.getPlace()
    const components = place?.address_components
    if (!components?.length) return

    const parsed = this.parseAddressComponents(components)

    this.fillingFromPlace = true
    try {
      this.street1Target.blur()
      this.applyParsedAddress(parsed)
      this.applyPlaceGeometry(place)
      this.closeSuggestionList()
    } finally {
      this.fillingFromPlace = false
    }
  }

  applyParsedAddress(parsed) {
    if (parsed.streetLine) {
      this.street1Target.value = parsed.streetLine
      this.street1Target.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (this.hasStreet2Target) {
      this.street2Target.value = parsed.subpremise || ""
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = parsed.city || ""
      this.cityTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (parsed.city && this.hasCitySelectTarget) {
      this.syncCitySelect(parsed.city)
    }
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = parsed.postalCode || ""
      this.postalCodeTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (parsed.state) {
      if (this.hasStateTarget) {
        this.stateTarget.value = parsed.state
        this.stateTarget.dispatchEvent(new Event("input", { bubbles: true }))
      }
      if (this.hasStateSelectTarget) {
        this.syncStateSelect(parsed.state)
      }
    }
  }

  applyPlaceGeometry(place) {
    if (!this.hasLatitudeTarget || !this.hasLongitudeTarget) return

    const loc = place.geometry?.location
    if (!loc) {
      this.clearCoordinates()
      return
    }

    const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat
    const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng
    if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
      this.clearCoordinates()
      return
    }

    this.latitudeTarget.value = Number(lat).toFixed(6)
    this.longitudeTarget.value = Number(lng).toFixed(6)
  }

  clearCoordinates() {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ""
  }

  bindStreet1SuggestionEvents() {
    this.unbindStreet1SuggestionEvents()
    this.boundStreet1Input = () => {
      if (this.fillingFromPlace) return
      this.clearCoordinates()
      this.openSuggestionList()
    }
    this.boundStreet1Focus = () => this.openSuggestionList()
    this.boundClearCoordinates = () => {
      if (this.fillingFromPlace) return
      this.clearCoordinates()
    }
    this.street1Target.addEventListener("input", this.boundStreet1Input)
    this.street1Target.addEventListener("focus", this.boundStreet1Focus)
    this.bindCoordinateInvalidation()
  }

  bindCoordinateInvalidation() {
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.addEventListener("input", this.boundClearCoordinates)
    }
    if (this.hasCitySelectTarget) {
      this.citySelectTarget.addEventListener("change", this.boundClearCoordinates)
    }
    if (this.hasCityTarget) {
      this.cityTarget.addEventListener("input", this.boundClearCoordinates)
    }
    if (this.hasStateSelectTarget) {
      this.stateSelectTarget.addEventListener("change", this.boundClearCoordinates)
    }
    if (this.hasStateTarget) {
      this.stateTarget.addEventListener("input", this.boundClearCoordinates)
    }
  }

  unbindStreet1SuggestionEvents() {
    if (!this.boundStreet1Input) return
    if (this.hasStreet1Target) {
      this.street1Target.removeEventListener("input", this.boundStreet1Input)
      this.street1Target.removeEventListener("focus", this.boundStreet1Focus)
    }
    if (this.boundClearCoordinates) {
      if (this.hasPostalCodeTarget) {
        this.postalCodeTarget.removeEventListener("input", this.boundClearCoordinates)
      }
      if (this.hasCitySelectTarget) {
        this.citySelectTarget.removeEventListener("change", this.boundClearCoordinates)
      }
      if (this.hasCityTarget) {
        this.cityTarget.removeEventListener("input", this.boundClearCoordinates)
      }
      if (this.hasStateSelectTarget) {
        this.stateSelectTarget.removeEventListener("change", this.boundClearCoordinates)
      }
      if (this.hasStateTarget) {
        this.stateTarget.removeEventListener("input", this.boundClearCoordinates)
      }
    }
    this.boundStreet1Input = null
    this.boundStreet1Focus = null
    this.boundClearCoordinates = null
  }

  // Google Autocomplete reopens .pac-container after we rewrite street 1 to the
  // street-only line; keep it hidden until the user focuses or types again.
  closeSuggestionList() {
    this.hidePacContainers()
    this.cancelHidePacFrame()
    this.hidePacFrame = requestAnimationFrame(() => {
      this.hidePacFrame = null
      this.hidePacContainers()
    })
  }

  openSuggestionList() {
    this.cancelHidePacFrame()
    document.querySelectorAll(".pac-container").forEach((el) => {
      el.classList.remove("pac-container-closed")
    })
  }

  cancelHidePacFrame() {
    if (!this.hidePacFrame) return
    cancelAnimationFrame(this.hidePacFrame)
    this.hidePacFrame = null
  }

  hidePacContainers() {
    document.querySelectorAll(".pac-container").forEach((el) => {
      el.classList.add("pac-container-closed")
    })
  }

  parseAddressComponents(components) {
    const data = {
      street_number: "",
      route: "",
      city: "",
      state: "",
      postalCode: "",
      subpremise: ""
    }

    for (const c of components) {
      const types = c.types
      if (types.includes("street_number")) data.street_number = c.long_name
      else if (types.includes("route")) data.route = c.long_name
      else if (types.includes("locality")) data.city = c.long_name
      else if (types.includes("sublocality") || types.includes("neighborhood")) {
        if (!data.city) data.city = c.long_name
      } else if (types.includes("administrative_area_level_1")) data.state = c.short_name
      else if (types.includes("postal_code")) data.postalCode = c.long_name
      else if (types.includes("subpremise")) data.subpremise = c.long_name
    }

    if (!data.city) {
      const level3 = components.find((x) => x.types.includes("administrative_area_level_3"))
      if (level3) data.city = level3.long_name
    }

    const streetLine = [data.street_number, data.route].filter(Boolean).join(" ").trim()
    return {
      streetLine,
      city: data.city,
      state: data.state,
      postalCode: data.postalCode,
      subpremise: data.subpremise
    }
  }

  syncStateSelect(abbrev) {
    const select = this.stateSelectTarget
    const option = Array.from(select.options).find((o) => o.value === abbrev)
    if (!option) return

    this.applySelection(select, option.value)
  }

  // City selects are backed by records, so the option value is an id and only the
  // visible text can be matched against the name Google returns.
  syncCitySelect(name) {
    const select = this.citySelectTarget
    const wanted = name.trim().toLowerCase()
    const option = Array.from(select.options).find((o) => o.text.trim().toLowerCase() === wanted)
    if (!option) return

    this.applySelection(select, option.value)
  }

  applySelection(select, value) {
    select.value = value
    select.dispatchEvent(new Event("change", { bubbles: true }))

    const wrap = select.closest("[data-controller~='custom-select']")
    const app = window.Stimulus
    if (wrap && app?.getControllerForElementAndIdentifier) {
      const ctrl = app.getControllerForElementAndIdentifier(wrap, "custom-select")
      if (ctrl) {
        ctrl.updateLabel()
        ctrl.highlightSelected(value)
      }
    }
  }
}
